import { usePutApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/usePutApplicationApplicantDetailsQuery";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { APPLICATION_RELATIONSHIP } from "@lending/types";
import type { PersonalInfoApiData } from "@lending/hooks/queries/PersonalInfo/types";

const noop = () => undefined;

function useUpdateCoApplicantDetailsQuery(
  options: {
    onSuccess?: () => Promise<void> | void;
    onError?: (error: Record<string, unknown>) => void;
  } = {}
) {
  const lendingApplicationID = useLendingApplicationID();
  const updateApplicationApplicantDetails = usePutApplicationApplicantDetailsQuery({
    onSuccess: options.onSuccess,
    onError: options.onError ?? noop,
  });

  const updateCoApplicantDetails = async ({
    applicantId,
    data,
    includeSensitiveFields,
  }: {
    applicantId: string;
    data: PersonalInfoApiData & { is_spouse?: boolean | null };
    includeSensitiveFields?: { tax_id?: boolean; date_of_birth?: boolean };
  }) => {
    if (!lendingApplicationID) {
      throw new Error("Missing application id for co-applicant update.");
    }

    await updateApplicationApplicantDetails.mutateAsync({
      params: {
        path: {
          uuid: applicantId,
        },
      },
      body: {
        application: lendingApplicationID,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        // Only include sensitive fields when the user actually changed them. Sending back the
        // masked SSN from the /sensitive endpoint would corrupt the stored value.
        ...(includeSensitiveFields?.tax_id ? { tax_id: data.tax_id } : {}),
        ...(includeSensitiveFields?.date_of_birth ? { date_of_birth: data.date_of_birth } : {}),
        // Only include is_spouse when the form provided it. Sending an explicit null when the
        // checkbox is disabled would clear any previously-saved value on the server.
        ...("is_spouse" in data ? { is_spouse: data.is_spouse } : {}),
        application_relationship: APPLICATION_RELATIONSHIP.CO_APPLICANT,
      },
    });
  };

  return {
    updateCoApplicantDetails,
    isPending: updateApplicationApplicantDetails.isPending,
  };
}

export { useUpdateCoApplicantDetailsQuery };
